import React, { useContext, useMemo } from "react";
import InstitutionContext from "../../../Context/InstitutionContext";
import Context from "../../../Context/Context";

const RevenueCard = ({
  currency,
  totalCollected = 0,
  transactionCount = 0,
  growthPercent = 0,
  isOffline = false,
  isDarkMode = true,
}) => {
  const getCurrencySymbol = (curr) => (curr === "USD" ? "$" : "₹");
  
  const formatAmount = (amount, minimumFractionDigits = 2) => {
    return `${getCurrencySymbol(currency)}${(amount / 100).toLocaleString(
      undefined,
      {
        minimumFractionDigits,
        maximumFractionDigits: 2,
      }
    )}`;
  };

  // Get icon based on card type
  const getIcon = () => {
    if (isOffline) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2h-2.5A2.5 2.5 0 0013 5m-3 6h3m3 0h3" />
        </svg>
      );
    } else if (currency === "USD") {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <text x="2" y="18" fontSize="20" fontWeight="bold">$</text>
        </svg>
      );
    } else {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <text x="2" y="18" fontSize="20" fontWeight="bold">₹</text>
        </svg>
      );
    }
  };

  // Determine card colors - Only Offline collection is yellow
  const isYellowCard = isOffline;
  const cardBg = isYellowCard ? 'bg-[#FDCF08]' : isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white';
  const textColor = isYellowCard ? 'text-black' : isDarkMode ? 'text-white' : 'text-black';
  const labelColor = isYellowCard ? 'text-black/70' : isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const iconBg = isYellowCard ? 'bg-black/10' : isDarkMode ? 'bg-[#FDCF08]/20' : 'bg-[#FDCF08]/10';
  const iconColor = isYellowCard ? 'text-black' : 'text-[#FDCF08]';
  const border =  !isDarkMode ? 'border border-gray-200' : '';
  const growthGreen = isYellowCard ? 'text-green-600' : 'text-green-400';
  const growthRed = isYellowCard ? 'text-red-600' : 'text-red-400';

  return (
    <div className={`${cardBg} rounded-lg p-3 md:p-4 transition-all duration-300 ${border}`} style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Header: Title and Icon */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className={`text-xs font-medium ${labelColor}`}>
            {isOffline ? "Offline Collection" : `${currency} Collection`}
          </p>
        </div>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <span className={`${iconColor} text-xs font-bold`}>
            {currency === "USD" ? "$" : currency === "INR" ? "₹" : "📱"}
          </span>
        </div>
      </div>

      {/* Main Amount */}
      <div className="mb-2">
        <p className={`text-2xl md:text-3xl font-bold ${textColor} leading-tight`}>
          {formatAmount(totalCollected)}
        </p>
      </div>

      {/* Growth Percentage */}
      <div className="flex items-center gap-2">
        <span className={`text-xs font-medium ${growthPercent >= 0 ? growthGreen : growthRed}`}>
          {growthPercent >= 0 ? '✓' : '–'} {Math.abs(growthPercent).toFixed(1)}% from last month
        </span>
      </div>
    </div>
  );
};


const RevenueSection = ({
  revenue = [],
  cashoutAmount,
  selectedYear,
  selectedMonth,
  isDarkMode = true,
}) => {
  // Define months array
  const months = useMemo(
    () => [
      "All time",
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  // Calculate USD stats
  const usdStats = useMemo(() => {
    const onlineUsdPayments =
      revenue?.filter(
        (payment) =>
          payment.paymentMode !== "offline" && payment.currency === "USD"
      ) || [];

    return onlineUsdPayments.reduce(
      (stats, payment) => {
        const amount = parseInt(payment.amount) || 0;
        if (!Number.isFinite(amount)) return stats;

        return {
          total: stats.total + amount,
          count: stats.count + 1,
          amounts: [...stats.amounts, amount],
        };
      },
      { total: 0, count: 0, amounts: [] }
    );
  }, [revenue]);

  // Calculate INR stats
  const inrStats = useMemo(() => {
    const onlineInrPayments =
      revenue?.filter(
        (payment) =>
          payment.paymentMode !== "offline" && payment.currency === "INR"
      ) || [];

    return onlineInrPayments.reduce(
      (stats, payment) => {
        const amount = parseInt(payment.amount) || 0;
        if (!Number.isFinite(amount)) return stats;

        return {
          total: stats.total + amount,
          count: stats.count + 1,
          amounts: [...stats.amounts, amount],
        };
      },
      { total: 0, count: 0, amounts: [] }
    );
  }, [revenue]);

  // Calculate offline stats
  const offlineStats = useMemo(() => {
    const offlinePayments =
      revenue?.filter((payment) => payment.paymentMode === "offline") || [];

    return offlinePayments.reduce(
      (stats, payment) => {
        const amount = parseInt(payment.amount) || 0;
        if (!Number.isFinite(amount)) return stats;

        return {
          total: stats.total + amount,
          count: stats.count + 1,
        };
      },
      { total: 0, count: 0 }
    );
  }, [revenue]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6">
      <RevenueCard
        currency="USD"
        totalCollected={usdStats.total}
        transactionCount={usdStats.count}
        growthPercent={12.5}
        isDarkMode={isDarkMode}
      />

      <RevenueCard
        currency="INR"
        totalCollected={inrStats.total}
        transactionCount={inrStats.count}
        growthPercent={48.2}
        isDarkMode={isDarkMode}
      />

      <RevenueCard
        currency="INR"
        totalCollected={offlineStats.total}
        transactionCount={offlineStats.count}
        growthPercent={0.0}
        isOffline={true}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default RevenueSection;